import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const NotificationForm = ({ open, onClose, onSubmit, initialData }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    sendDate: null,
    sendTo: 'ALL',
  });

  const isNotificationPending = initialData ? initialData.status === 'PENDING' : true;
  const isPastDate =
    initialData?.sendDate && dayjs(initialData.sendDate).isBefore(dayjs());
  const isDisabled = !isNotificationPending || isPastDate;

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        content: initialData.content || '',
        sendDate: initialData.sendDate ? dayjs(initialData.sendDate) : null,
        sendTo: 'ALL',
      });
    } else {
      // Reset form when opening for new notification
      setFormData({
        title: '',
        content: '',
        sendDate: null,
        sendTo: 'ALL',
      });
    }
  }, [initialData, open]);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, sendDate: date });
  };

  const handleSubmit = () => {
    onSubmit(formData);
    onClose();
  };

  const getDisabledMessage = () => {
    if (initialData?.status === 'SENT') {
      return 'This notification has already been sent and cannot be modified';
    }
    if (initialData?.status === 'CANCELED') {
      return 'This notification has been canceled/deleted and cannot be modified';
    }
    if (isPastDate) {
      return "This notification's send date is in the past and cannot be modified";
    }
    return '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialData ? 'Edit Notification' : 'Add Notification'}
      </DialogTitle>
      <DialogContent>
        {isDisabled && (
          <Typography color="error" sx={{ mb: 2 }}>
            {getDisabledMessage()}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <Tooltip
            title={isDisabled ? 'Cannot modify sent or past notifications' : ''}
          >
            <TextField
              label="Title"
              value={formData.title}
              onChange={handleChange('title')}
              fullWidth
              required
              disabled={isDisabled}
              sx={{
                '& .MuiInputBase-input:not(:disabled)': { color: 'black' },
              }}
            />
          </Tooltip>
          <Tooltip
            title={isDisabled ? 'Cannot modify sent or past notifications' : ''}
          >
            <TextField
              label="Content"
              value={formData.content}
              onChange={handleChange('content')}
              fullWidth
              multiline
              rows={4}
              required
              disabled={isDisabled}
              sx={{
                '& .MuiInputBase-input:not(:disabled)': { color: 'black' },
              }}
            />
          </Tooltip>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
              <Tooltip
                title={
                  isDisabled ? 'Cannot modify sent or past notifications' : ''
                }
              >
                <Box>
                  <DateTimePicker
                    label="Send Date & Time"
                    value={formData.sendDate}
                    onChange={handleDateChange}
                    ampm={true}
                    format="MM/DD/YYYY hh:mm A"
                    minDateTime={dayjs()}
                    disablePast
                    disabled={isDisabled}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'black !important',
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            </Box>
          </LocalizationProvider>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isDisabled}
        >
          {initialData ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationForm;
